import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import {
  ListingSectionConfig,
  ListingSectionId,
  SectionVisibilityFlags,
  DEFAULT_LISTING_SECTIONS_CONFIG,
} from 'domain/listing/types/listing-sections-config';

/**
 * Transformed listing section data ready for presentation.
 * 
 * **Domain-Specific**: This represents listing sections with translated labels.
 */
export interface ListingSectionData {
  /** Section identifier (e.g., ListingSectionId.OVERVIEW, ListingSectionId.PRICE_HISTORY) */
  id: ListingSectionId;
  
  /** Translated section label */
  label: string;
  
  /** Icon identifier for UI layer to map to components */
  iconName: string;
}

/**
 * Props for useListingSections hook.
 */
export interface UseListingSectionsProps {
  /** Flags indicating which sections have available data */
  visibilityFlags: SectionVisibilityFlags;
  
  /** Optional custom section configuration (defaults to DEFAULT_LISTING_SECTIONS_CONFIG) */
  config?: ListingSectionConfig[];
}

/**
 * Domain hook that bridges listing section configuration to presentation layer.
 * 
 * **Responsibilities:**
 * 1. Filters sections based on data availability (conditional sections)
 * 2. Translates section labels using i18n
 * 3. Transforms domain config into presentation-ready data
 * 
 * **Domain Logic:** "Which sections should be shown for THIS listing?"
 * 
 * @example
 * ```tsx
 * function MyListingComponent({ listing }) {
 *   const sections = useListingSections({
 *     visibilityFlags: {
 *       shouldShowPriceHistory: listing.hasPriceHistory,
 *       shouldShowAppraisal: listing.hasAppraisal,
 *       shouldShowSimilarListings: true,
 *       shouldShowCommute: true,
 *     },
 *   });
 *   
 *   return <StickyHeaderTabs sections={sections} />;
 * }
 * ```
 */
export function useListingSections({
  visibilityFlags,
  config = DEFAULT_LISTING_SECTIONS_CONFIG,
}: UseListingSectionsProps): ListingSectionData[] {
  const { t } = useTranslation('listing');

  return useMemo(() => {
    return config
      .filter(section => {
        // Always show non-conditional sections
        if (!section.isConditional) return true;
        
        // Show conditional sections only if visibility flag is true
        return section.visibilityKey && visibilityFlags[section.visibilityKey];
      })
      .map(section => ({
        id: section.id,
        label: t(section.labelKey), // i18n happens here
        iconName: section.iconName,
      }));
  }, [config, visibilityFlags, t]);
}
